'use client';

import React, { useEffect, useRef, useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Drawer,
  DrawerContent,
  DrawerTitleHeader,
} from '@/components/ui/drawer';
import { useTranslations } from 'next-intl';
import IconFont from '@/components/ui/iconFont';
import { track } from '@/utils/track';
import { getFaqList } from './utils';

function getTrackCurrentPage(type?: string) {
  if (type === 'deposit') return 'deposit details';
  if (type === 'withdraw') return 'withdraw details';
  return undefined;
}

const DepositFaqModal = NiceModal.create(({ itemKey, type }: { itemKey: string; type?: string; }) => {
  const t = useTranslations('footer');
  const modal = useModal();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set([]));
  const contentRef = useRef(null);
  const itemHeightMap = useRef({});
  const list = getFaqList(t, type);

  const toggleExpanded = (key: string) => {
    track('recharge_withdraw_faq_click', { current_page: getTrackCurrentPage(type) });
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setExpandedItems(new Set([itemKey]));
    setTimeout(() => {
      if (itemKey && contentRef.current) {
        const contentElem = contentRef.current as HTMLDivElement;
        const items = contentElem.querySelectorAll('.faq-list-item');
        let top = 0;
        if (items.length) {
          for (let i = 0; i < items.length; i++) {
            const item = items[i] as HTMLDivElement;
            const dataset = item.dataset;
            if (dataset.key && String(dataset.key) === String(itemKey)) {
              break;
            }
            if (dataset.key && String(dataset.key) !== String(itemKey)) {
              if (!itemHeightMap[dataset.key]) {
                itemHeightMap[dataset.key] = item.offsetHeight;
              }
              top += itemHeightMap[dataset.key];
            }
          }
        }
        if (top > 0) {
          top = Math.min(contentElem.scrollHeight - contentElem.offsetHeight, top);
        }
        (contentRef.current as HTMLDivElement).scrollTo({
          top
        });
      }
    }, 0);

    return () => {
      setExpandedItems(new Set([]));
    };
  }, [itemKey]);

  return (
    <Drawer
      open={modal.visible}
      onOpenChange={(open) => !open && modal.remove()}
    >
      <DrawerContent className="bg-[#181D29] border-none rounded-t-[10px] z-[900] text-white max-h-[80vh]">
        <DrawerTitleHeader title={t('faq.title')} showClose />
        <div ref={contentRef} className="relative flex flex-col flex-1 pb-[20px] overflow-y-auto hide-scrollbar">
          <div className="px-[12px]">
            <div className='bg-[#131720] pb-[20px] rounded-[8px]'>
              {
                list.map((item, index) => {
                  const isExpanded = expandedItems.has(item.key);
                  return (
                    <div key={index} className={`flex flex-col pt-[20px] faq-list-item`} data-key={item.key}>
                      <div className="px-[20px] pb-[20px]">
                        {
                          item.title && (
                            <>
                              <div
                                className="flex justify-between items-center w-full cursor-pointer"
                                onClick={() => toggleExpanded(item.key)}
                              >
                                <span className="text-[#fff] text-[13px] font-medium leading-[16px] pr-[10px]">{item.title}</span>
                                <IconFont
                                  name="arrow-up"
                                  size={10}
                                  color="#a9b5cb"
                                  className={`mt-[4px] transition-transform duration-200 ${!isExpanded ? 'rotate-180' : ''
                                    }`}
                                />
                              </div>
                            </>
                          )
                        }
                        {isExpanded && (
                          <div className="text-[#748094] text-[12px] leading-[16px] pt-[14px] font-medium">
                            {item.description}
                          </div>
                        )}

                      </div>
                      <div className="bg-[rgba(214,234,255,0.1)] h-[1px] mx-[20px]" />
                    </div>
                  );
                })
              }
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
});

export const showDepositFaqModal = ({ itemKey, type }: { itemKey?: string; type?: string; }) => {
  return NiceModal.show(DepositFaqModal, {
    itemKey,
    type
  });
};

export const hideDepositFaqModal = () => {
  return NiceModal.hide(DepositFaqModal);
};